import { useEffect, useRef, useState } from 'react'
import type { DockMode } from '../plan/useAssistantDock'
import { Icon } from './Icon'

export const DOCK_LABEL: Record<DockMode, string> = {
  closed: 'Closed',
  docked: 'In the console',
  float: 'Floating',
  right: 'Pinned right',
}

// PlacementMenu — where the conversation sits, offered from wherever it
// currently is. It travels WITH the chat: docking used to remove every control
// that could undock it, which made "docked" a one-way door.
export function PlacementMenu({
  mode,
  onPick,
  onClose,
}: {
  mode: DockMode
  onPick: (m: DockMode) => void
  onClose: () => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const down = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    const key = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    window.addEventListener('mousedown', down)
    window.addEventListener('keydown', key)
    return () => {
      window.removeEventListener('mousedown', down)
      window.removeEventListener('keydown', key)
    }
  }, [open])

  return (
    <div className="ap-controls" ref={ref}>
      <button
        className="ap-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        title={`Placement — ${DOCK_LABEL[mode]}`}
      >
        <Icon name="placement" size={15} />
      </button>
      <button className="ap-btn" onClick={onClose} title="Close the chat">
        <span aria-hidden>✕</span>
        <span className="sr-only">Close the chat</span>
      </button>
      {open ? (
        <div className="ap-menu" role="menu">
          {(['docked', 'float', 'right'] as DockMode[]).map((m) => (
            <button
              key={m}
              role="menuitemradio"
              aria-checked={mode === m}
              className={`ap-menu-item${mode === m ? ' on' : ''}`}
              onClick={() => {
                onPick(m)
                setOpen(false)
              }}
            >
              {DOCK_LABEL[m]}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
